import React, { useState, useEffect } from 'react';
import './SuperAdministator.css';
import { CustomSelect } from './CustomSelect.js';
import './CustomSelect.css';
import * as XLSX from 'xlsx';
import { createPortal } from 'react-dom';
import { Edit2, Trash2, Plus, Search, Filter, Download, Upload, RefreshCw, Check, X, ChevronLeft, ChevronRight, FileDown } from 'lucide-react';

const ROLES = ['System Administrator', 'Broadcast', 'AskHR Analytics', 'Case Management', 'Pinned Users Report'];
const STATUSES = ['Active', 'Inactive'];
const ITEMS_PER_PAGE = 6;


export default function Super_Administator() {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Backend data states
  const [liveCountries, setLiveCountries] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);

  // ── Logged-in admin's assigned countries (from Cosmos role assignments) ──
  const [adminAssignedCountries, setAdminAssignedCountries] = useState([]);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [loadingAdminCountries, setLoadingAdminCountries] = useState(false);

  // Edit state - only status and endDate are editable
  const [editFormData, setEditFormData] = useState({
    status: '',
    endDate: ''
  });

  // Form states for Add User Modal
  const [formData, setFormData] = useState({
    role: '',
    email: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'Active',
    country: ''
  });
  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadRows, setUploadRows] = useState([]);       // { ...fields, _valid, _errors }
  const [uploadParsed, setUploadParsed] = useState(false);
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadResults, setUploadResults] = useState([]); 

  // Fetch role assignments on component mount
  useEffect(() => {
    fetchRoleAssignments();
    fetchLiveCountries();
    fetchAdminAssignedCountries(); // ← fetch admin's own country access
  }, []);

  // Fetch group members when modal opens
  useEffect(() => {
    if (showAddModal) {
      fetchGroupMembers();
    }
  }, [showAddModal]);

  // ============================================
  // FETCH LOGGED-IN ADMIN'S ASSIGNED COUNTRIES
  // ============================================
  const fetchAdminAssignedCountries = async () => {
    setLoadingAdminCountries(true);
    try {
      const response = await fetch('/api/user-assigned-countries/role-management', {
        credentials: 'include'
      });

      if (!response.ok) {
        console.warn(' Could not fetch admin assigned countries, defaulting to global');
        setIsGlobalAdmin(true);
        return;
      }

      const data = await response.json();
      console.log('👤 Admin assigned countries:', data);

      setIsGlobalAdmin(data.isGlobal);
      setAdminAssignedCountries(data.assignedCountries || []);
    } catch (err) {
      console.error(' Error fetching admin countries:', err);
      // On error, default to showing all users (safe fallback)
      setIsGlobalAdmin(true);
    } finally {
      setLoadingAdminCountries(false);
    }
  };

  // ============================================
  // CALCULATE STATUS BASED ON DATE RANGE
  // ============================================
  const calculateStatus = (startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    // If no end date, check if start date has passed
    if (!endDate) {
      const status = start <= today ? 'Active' : 'Inactive';
      console.log(`📅 Calculate Status (No End Date): Start=${startDate}, Today=${today.toISOString()}, Result=${status}`);
      return status;
    }

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    // Check if today is within the date range
    let status;
    if (today >= start && today <= end) {
      status = 'Active';
    } else {
      status = 'Inactive';
    }

    console.log(`📅 Calculate Status: Start=${startDate}, End=${endDate}, Today=${today.toISOString().split('T')[0]}, Result=${status}`);
    return status;
  };

  // ============================================
  // CHECK IF DATE HAS EXPIRED
  // ============================================
  const isDateExpired = (endDate) => {
    if (!endDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    return today > end;
  };

  // ============================================
  // UPDATE STATUS IN DATABASE
  // ============================================
  const updateStatusInDatabase = async (role, newStatus) => {
    console.log(`🔄 Attempting to update status for ${role.email} from "${role.status}" to "${newStatus}"`);

    try {
      const payload = {
        role: role.role,
        email: role.email,
        startDate: role.startDate,
        endDate: role.endDate,
        status: newStatus,
        country: role.country
      };

      console.log('📤 Sending PUT request:', payload);
      console.log('📤 URL:', `/api/role-assignments/${role.id}?email=${encodeURIComponent(role.email)}`);

      const response = await fetch(`/api/role-assignments/${role.id}?email=${encodeURIComponent(role.email)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(' Response error:', errorText);
        throw new Error(`Failed to update status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(' Status auto-updated successfully:', result);
      return true;
    } catch (err) {
      console.error(' Error auto-updating status:', err);
      return false;
    }
  };

  // ============================================
  // FETCH ROLE ASSIGNMENTS FROM COSMOS
  // ============================================
  const fetchRoleAssignments = async () => {
    setLoadingUsers(true);
    setError(null);

    try {
      const response = await fetch('/api/role-assignments', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch role assignments');
      }

      const data = await response.json();

      const transformedUsers = [];
      let updateCount = 0;

      for (const role of data) {
        //  KEY FIX: Only auto-update to Inactive (never force back to Active)
        // If DB says Inactive, ALWAYS respect it — don't recalculate over it
        let resolvedStatus = role.status;

        // Only override if DB says Active but the date has actually expired
        if (role.status === 'Active' && isDateExpired(role.endDate)) {
          resolvedStatus = 'Inactive';
          const updateSuccess = await updateStatusInDatabase(role, 'Inactive');
          if (updateSuccess) updateCount++;
        }

        transformedUsers.push({
          id: role.id,
          role: role.role,
          email: role.email,
          startDate: role.startDate,
          endDate: role.endDate,
          status: resolvedStatus,
          countries: [role.country]
        });
      }

      if (updateCount > 0) {
        console.log(` Auto-deactivated ${updateCount} expired role assignment(s)`);
      }

      setUsers(transformedUsers.sort((a, b) => a.role.localeCompare(b.role)));
    } catch (err) {
      console.error(' Error fetching role assignments:', err);
      setError('Failed to load role assignments');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchLiveCountries = async () => {
    setLoadingCountries(true);
    setError(null);
    try {
      const response = await fetch('/api/golive-countries', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch countries');
      }

      const data = await response.json();

      if (data.success) {
        setLiveCountries(['Global', ...data.countries]);
        console.log(' Live countries fetched:', data.countries);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error(' Error fetching countries:', err);
      setError('Failed to load countries');
      setLiveCountries(['Global', 'Philippines', 'Indonesia', 'USA', 'Canada', 'UK']);
    } finally {
      setLoadingCountries(false);
    }
  };

  // const fetchGroupMembers = async () => {
  //   setLoadingMembers(true);
  //   setError(null);
  //   try {
  //     const response = await fetch('/SSO_Cred', {
  //       credentials: 'include'
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch group members');
  //     }

  //     const data = await response.json();

  //     if (data.data && data.data.groupMembers) {
  //       const members = data.data.groupMembers.map(member => ({
  //         email: member.mail || member.userPrincipalName,
  //         displayName: member.displayName,
  //         id: member.id,
  //         country: member.country || ''   // ← now included from Graph API
  //       })).filter(member => member.email);
  //       console.log(' All users fetched:', members.length);
  //       setGroupMembers(members.sort((a, b) => a.displayName.localeCompare(b.displayName)));
  //     } else if (data.data && data.data.allUsers) {
  //       const members = data.data.allUsers.map(user => ({
  //         email: user.mail || user.userPrincipalName,
  //         displayName: user.displayName,
  //         id: user.id,
  //         country: user.country || ''     // ← now included from Graph API
  //       })).filter(member => member.email);
  //       console.log(' All users fetched:', members.length);
  //       setGroupMembers(members.sort((a, b) => a.displayName.localeCompare(b.displayName)));
  //     }
  //     else {
  //       throw new Error('Invalid response format');
  //     }
  //   } catch (err) {
  //     console.error(' Error fetching group members:', err);
  //     setError('Failed to load group members');
  //     setGroupMembers([]);
  //   } finally {
  //     setLoadingMembers(false);
  //   }
  // };

  const fetchGroupMembers = async () => {
  setLoadingMembers(true);
  setError(null);
  
  let attempts = 0;
  const maxAttempts = 8;          // ~8 seconds max wait
  const retryDelay  = 1000;       // 1 second between retries

  const tryFetch = async () => {
    attempts++;
    try {
      const response = await fetch('/SSO_Cred', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch group members');

      const data = await response.json();
      const source = data.data?.groupMembers?.length > 0
        ? data.data.groupMembers
        : data.data?.allUsers || [];

      if (source.length === 0 && attempts < maxAttempts) {
        // Cache still warming — wait and retry
        console.log(`[Members] Cache warming, retry ${attempts}/${maxAttempts}...`);
        await new Promise(r => setTimeout(r, retryDelay));
        return tryFetch();
      }

      const members = source.map(member => ({
        email: member.mail || member.userPrincipalName,
        displayName: member.displayName,
        id: member.id,
        country: member.country || ''
      })).filter(m => m.email);

      console.log(`✅ Members loaded: ${members.length} (after ${attempts} attempt(s))`);
      setGroupMembers(members.sort((a, b) => a.displayName.localeCompare(b.displayName)));

    } catch (err) {
      if (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, retryDelay));
        return tryFetch();
      }
      console.error('❌ Error fetching group members:', err);
      setError('Failed to load group members');
      setGroupMembers([]);
    } finally {
      if (attempts >= maxAttempts || groupMembers.length > 0) {
        setLoadingMembers(false);
      }
    }
  };

  await tryFetch();
  setLoadingMembers(false);
  };

  // ============================================
  // FILTER GROUP MEMBERS BY ADMIN'S AD COUNTRY ACCESS
  // ============================================

const getFilteredEmailOptions = () => {
  // Global admin → show everyone
  if (isGlobalAdmin) {
    return groupMembers.map(m => ({
      value: m.email,
      label: `${m.displayName} (${m.email})`
    }));
  }

  // Country-level admin → only show AD users whose country matches
  const assignedLower = adminAssignedCountries.map(c => c.toLowerCase().trim());

  const filtered = groupMembers.filter(m => {
    const memberCountry = (m.country || '').toLowerCase().trim();
    if (!memberCountry) return false;
    return assignedLower.some(assigned => assigned === memberCountry);
  });

  console.log(
    `🔍 Email filter: ${filtered.length} of ${groupMembers.length} users match assigned countries [${adminAssignedCountries.join(', ')}]`
  );

  return filtered.map(m => ({
    value: m.email,
    label: `${m.displayName} (${m.email})${m.country ? ' — ' + m.country : ''}`
  }));
};

// ============================================
// FILTER COUNTRIES BY ADMIN'S ASSIGNED COUNTRIES
// ============================================
const getFilteredCountryOptions = () => {
  // Global admin → show all live countries
  if (isGlobalAdmin) {
    return liveCountries.map(c => ({ value: c, label: c }));
  }

  // Country-level admin → only show their assigned countries
  // exclude 'Global' — they can't assign Global access to others
  return adminAssignedCountries
    .filter(c => c.toLowerCase() !== 'global')
    .map(c => ({ value: c, label: c }));
};

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'All' || user.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  const toggleSelectUser = (id) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === currentUsers.length && currentUsers.length > 0) {
      const newSelected = new Set(selectedUsers);
      currentUsers.forEach(user => newSelected.delete(user.id));
      setSelectedUsers(newSelected);
    } else {
      const newSelected = new Set(selectedUsers);
      currentUsers.forEach(user => newSelected.add(user.id));
      setSelectedUsers(newSelected);
    }
  };

  const isAllCurrentPageSelected = () => {
    return currentUsers.length > 0 && currentUsers.every(user => selectedUsers.has(user.id));
  };

  // ============================================
  // EDIT FUNCTIONALITY
  // ============================================
  const handleEditClick = (user) => {
    console.log('✏️ Edit clicked for:', user.email, 'Current status:', user.status);
    setEditingId(user.id);
    setEditFormData({
      status: user.status,
      endDate: user.endDate || ''
    });
  };

  const handleCancelEdit = () => {
    console.log('❌ Edit cancelled');
    setEditingId(null);
    setEditFormData({
      status: '',
      endDate: ''
    });
  };

  const handleSaveEdit = async (user) => {
    console.log('💾 Save clicked - Attempting to save:', editFormData);

    // Validation: Check if trying to set Active when date has expired
    if (editFormData.status === 'Active' && isDateExpired(editFormData.endDate)) {
      alert('Cannot set status to Active: End date has passed');
      console.log(' Blocked: Cannot set Active - date expired');
      return;
    }

    // Validation: Check start date is before end date
    if (user.startDate && editFormData.endDate) {
      const start = new Date(user.startDate);
      const end = new Date(editFormData.endDate);
      if (start > end) {
        alert('End date cannot be before start date');
        return;
      }
    }

    try {
      const payload = {
        role: user.role,
        email: user.email,
        startDate: user.startDate,
        endDate: editFormData.endDate || null,
        status: editFormData.status,
        country: user.countries[0]
      };

      console.log('📤 Sending manual update:', payload);
      console.log('📤 URL:', `/api/role-assignments/${user.id}?email=${encodeURIComponent(user.email)}`);

      const response = await fetch(`/api/role-assignments/${user.id}?email=${encodeURIComponent(user.email)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('📥 Manual update response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to update: ${response.status}`);
      }

      const result = await response.json();
      console.log(' Manual update successful:', result);

      // Refresh the data
      await fetchRoleAssignments();

      // Reset edit state
      setEditingId(null);
      setEditFormData({ status: '', endDate: '' });

    } catch (err) {
      console.error('❌ Error in manual update:', err);
      alert('Failed to update: ' + err.message);
    }
  };


  // ============================================
  // DELETE ROLE ASSIGNMENT
  // ============================================
  const handleDelete = async (id, email) => {
    if (!window.confirm('Are you sure you want to delete this role assignment?')) {
      return;
    }

    try {
      const response = await fetch(`/api/role-assignments/${id}?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete role assignment');
      }

      await fetchRoleAssignments();
      selectedUsers.delete(id);
      setSelectedUsers(new Set(selectedUsers));

      console.log(' Role assignment deleted');
    } catch (err) {
      console.error('❌ Error deleting role assignment:', err);
      alert('Failed to delete role assignment: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedUsers.size} selected users?`)) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedUsers).map(id => {
        const user = users.find(u => u.id === id);
        if (user) {
          return fetch(`/api/role-assignments/${id}?email=${encodeURIComponent(user.email)}`, {
            method: 'DELETE',
            credentials: 'include'
          });
        }
        return Promise.resolve();
      });

      await Promise.all(deletePromises);
      await fetchRoleAssignments();
      setSelectedUsers(new Set());

      console.log(' Bulk delete completed');
    } catch (err) {
      console.error('❌ Error bulk deleting:', err);
      alert('Failed to delete some role assignments');
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ============================================
  // SUBMIT NEW USER (CREATE IN COSMOS)
  // ============================================
  const handleSubmitUser = async () => {
    // Validation
    if (!formData.role || !formData.email || !formData.startDate || !formData.country) {
      alert('Please fill in all required fields (Role, Email, Start Date, and Country)');
      return;
    }

    try {
      const response = await fetch('/api/role-assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          role: formData.role,
          email: formData.email,
          startDate: formData.startDate,
          endDate: formData.endDate || null,
          status: formData.status,
          country: formData.country
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create role assignment');
      }

      const result = await response.json();
      console.log(' Role assignment created:', result);

      await fetchRoleAssignments();

      setFormData({
        role: '',
        email: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        status: 'Active',
        country: ''
      });
      setShowAddModal(false);

      alert('Role assignment created successfully!');
    } catch (err) {
      console.error('❌ Error creating role assignment:', err);
      alert('Failed to create role assignment: ' + err.message);
    }
  };

  const handleEmailSelect = (e) => {
    const selectedEmail = e.target.value;
    if (selectedEmail) {
      setFormData(prev => ({
        ...prev,
        email: selectedEmail
      }));
    }
  };

  // ============================================
  // DOWNLOAD — calls backend endpoint
  // ============================================
  const handleDownload = () => {
    window.location.href = '/api/role-assignments/download';
  };

  // ============================================
// DOWNLOAD TEMPLATE — with dropdowns for Role & Country
// ============================================
const handleDownloadTemplate = async () => {
  if (liveCountries.length === 0) {
    alert('Countries are still loading. Please try again in a moment.');
    return;
  }

  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();

  // ── Hidden sheet: lists for dropdown sources ──
  const listsSheet = wb.addWorksheet('_lists');
  listsSheet.state = 'veryHidden';

  ROLES.forEach((role, i) => {
    listsSheet.getCell(i + 1, 1).value = role;
  });

  liveCountries.forEach((country, i) => {
    listsSheet.getCell(i + 1, 2).value = country;
  });

  // ── Main template sheet ──
  const sheet = wb.addWorksheet('Role Assignments');

  sheet.columns = [
    { header: 'role',      key: 'role',      width: 30 },
    { header: 'email',     key: 'email',     width: 35 },
    { header: 'startDate', key: 'startDate', width: 15 },
    { header: 'endDate',   key: 'endDate',   width: 15 },
    { header: 'status',    key: 'status',    width: 12 },
    { header: 'country',   key: 'country',   width: 20 },
  ];

  // Style header row
  const headerRow = sheet.getRow(1);
  headerRow.eachCell(cell => {
    cell.font      = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border    = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
  });
  headerRow.height = 20;

  // ── Date serial for today (Excel date format) ──
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todaySerial = Math.floor((today - new Date(1899, 11, 30)) / (24 * 60 * 60 * 1000));

  const roleFormula    = `_lists!$A$1:$A$${ROLES.length}`;
  const countryFormula = `_lists!$B$1:$B$${liveCountries.length}`;
  const statusFormula  = '"Active,Inactive"';

  // ── Apply validations as column-wide ranges ──
  sheet.dataValidations.add('A2:A1048576', {
    type: 'list',
    allowBlank: true,
    formulae: [roleFormula],
    showErrorMessage: true,
    errorTitle: 'Invalid Role',
    error: 'Please select a role from the dropdown list.',
    showInputMessage: true,
    promptTitle: 'Role',
    prompt: 'Select a role from the dropdown.',
  });

  sheet.dataValidations.add('C2:C1048576', {
    type: 'date',
    operator: 'greaterThanOrEqual',
    allowBlank: true,
    formulae: [todaySerial],
    showErrorMessage: true,
    errorTitle: 'Invalid Start Date',
    error: 'Start date must be today or a future date.',
    showInputMessage: true,
    promptTitle: 'Start Date',
    prompt: "Enter today's date or a future date (mm-dd-yyyy)",
  });

  sheet.dataValidations.add('D2:D1048576', {
    type: 'date',
    operator: 'greaterThanOrEqual',
    allowBlank: true,
    formulae: [todaySerial],
    showErrorMessage: true,
    errorTitle: 'Invalid End Date',
    error: 'End date must be today or a future date.',
    showInputMessage: true,
    promptTitle: 'End Date',
    prompt: 'Enter an optional end date (mm-dd-yyyy)',
  });

  sheet.dataValidations.add('E2:E1048576', {
    type: 'list',
    allowBlank: true,
    formulae: [statusFormula],
    showErrorMessage: true,
    errorTitle: 'Invalid Status',
    error: 'Please select Active or Inactive.',
    showInputMessage: true,
    promptTitle: 'Status',
    prompt: 'Select Active or Inactive.',
  });

  sheet.dataValidations.add('F2:F1048576', {
    type: 'list',
    allowBlank: true,
    formulae: [countryFormula],
    showErrorMessage: true,
    errorTitle: 'Invalid Country',
    error: 'Please select a country from the dropdown list.',
    showInputMessage: true,
    promptTitle: 'Country',
    prompt: 'Select a country from the dropdown.',
  });

  // ── Date format for start/end date columns ──
  sheet.getColumn(3).numFmt = 'mm-dd-yyyy';
  sheet.getColumn(4).numFmt = 'mm-dd-yyyy';

  // ── Freeze header row ──
  sheet.views = [{ state: 'frozen', ySplit: 1 }];

  // ── Export ──
  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = 'Role_Assignment_Template.xlsx';
  a.click();
  URL.revokeObjectURL(url);
};

  // ============================================
// UPLOAD — parse & validate Excel
// ============================================
const REQUIRED_COLUMNS = ['role', 'email', 'startDate', 'country'];

const normalizeHeader = (h) =>
  h.trim().toLowerCase().replace(/\s+/g, '').replace(/_/g, '');

const HEADER_MAP = {
  role: 'role',
  email: 'email',
  startdate: 'startDate',
  enddate: 'endDate',
  status: 'status',
  country: 'country',
  '#': null,
  'startDate': 'startDate',
  'endDate': 'endDate',
};

const parseAndValidateExcel = (file, validCountries = [], validEmails = []) => {
  const reader = new FileReader();

  const validCountrySet = new Set(validCountries.map(c => c.toLowerCase().trim()));
  const validEmailSet   = new Set(validEmails.map(e => e.toLowerCase().trim()));

  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });

      const visibleSheetName = workbook.SheetNames.find(name => !name.startsWith('_'));
      const sheet = workbook.Sheets[visibleSheetName || workbook.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

      if (raw.length < 2) {
        alert('The Excel file appears to be empty or has no data rows.');
        return;
      }

      const headers = raw[0].map(h => {
        if (h === null || h === undefined || h === '') return null;
        return HEADER_MAP[normalizeHeader(String(h))] || null;
      });
      
      const missingRequired = REQUIRED_COLUMNS.filter(req => !headers.includes(req));
      if (missingRequired.length > 0) {
        alert(`Missing required columns: ${missingRequired.join(', ')}`);
        return;
      }

      const validRoles = new Set(ROLES);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const rows = raw.slice(1).map((row, idx) => {
        const obj = {};
        headers.forEach((key, i) => {
          if (!key) return;
          const val = row[i];
          if (val instanceof Date) {
            const y = val.getFullYear();
            const m = String(val.getMonth() + 1).padStart(2, '0');
            const d = String(val.getDate()).padStart(2, '0');
            obj[key] = `${y}-${m}-${d}`;
          } else {
            obj[key] = String(val ?? '').trim();
          }
        });

        const errors = [];

        if (!obj.role) errors.push('Role is required');
        else if (!validRoles.has(obj.role)) errors.push(`Invalid role "${obj.role}"`);

        if (!obj.email) {
          errors.push('Email is required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(obj.email)) {
          errors.push('Invalid email format');
        } else if (validEmailSet.size > 0 && !validEmailSet.has(obj.email.toLowerCase().trim())) {
          errors.push(`"${obj.email}" not found in Active Directory`);
        }

        if (!obj.startDate) {
          errors.push('Start date is required');
        } else {
          const d = new Date(obj.startDate);
          if (isNaN(d.getTime())) errors.push('Invalid start date');
        }

        if (obj.endDate) {
          const d = new Date(obj.endDate);
          if (isNaN(d.getTime())) errors.push('Invalid end date');
          else if (obj.startDate && new Date(obj.startDate) > d)
            errors.push('End date before start date');
        }

        if (!obj.country) {
          errors.push('Country is required');
        } else if (
          validCountrySet.size > 0 &&
          !validCountrySet.has(obj.country.toLowerCase().trim()) &&
          obj.country.toLowerCase().trim() !== 'global'
        ) {
          errors.push(`"${obj.country}" is not a live country`);
        }

        if (!obj.status) obj.status = 'Active';
        else if (!['Active', 'Inactive'].includes(obj.status)) obj.status = 'Active';

        return {
          _rowNum: idx + 2,
          role: obj.role || '',
          email: obj.email || '',
          startDate: obj.startDate || '',
          endDate: obj.endDate || '',
          status: obj.status || 'Active',
          country: obj.country || '',
          _valid: errors.length === 0,
          _errors: errors,
        };
      }).filter(r => {
        const hasData = Object.entries(r)
          .filter(([k]) => !k.startsWith('_') && k !== 'status')
          .some(([, v]) => v !== '');
        if (!hasData) return false;

        const isSummaryRow = ['role', 'email', 'startDate', 'country'].some(field => {
          const val = String(r[field] || '').trim();
          return /^(total|active|inactive):/i.test(val);
        });
        if (isSummaryRow) return false;

        return true;
      });

      setUploadRows(rows);
      setUploadParsed(true);
    } catch (err) {
      console.error('Excel parse error:', err);
      alert('Failed to parse Excel file: ' + err.message);
    }
  };
  reader.readAsArrayBuffer(file);
};

const handleUploadFileChange = (file) => {
  if (!file) return;
  const ext = file.name.split('.').pop().toLowerCase();
  if (!['xlsx', 'xls'].includes(ext)) {
    alert('Please upload an Excel file (.xlsx or .xls)');
    return;
  }
  setUploadFile(file);
  setUploadFileName(file.name);
  setUploadParsed(false);
  setUploadRows([]);
  setUploadDone(false);

  parseAndValidateExcel(
    file,
    liveCountries,
    groupMembers.map(m => m.email)
  );
};

const handleUploadConfirm = async () => {
  const validRows = uploadRows.filter(r => r._valid);
  if (validRows.length === 0) return;

  setUploadSubmitting(true);
  const results = [];

  for (const row of validRows) {
    try {
      const response = await fetch('/api/role-assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role: row.role,
          email: row.email,
          startDate: row.startDate,
          endDate: row.endDate || null,
          status: row.status,
          country: row.country,
        }),
      });

      if (response.ok) {
        results.push({ row, status: 'success', message: 'Added successfully' });

      } else if (response.status === 409) {

        let existing = users.find(
          u =>
            u.email.toLowerCase() === row.email.toLowerCase() &&
            u.role === row.role &&
            u.countries[0]?.toLowerCase() === row.country?.toLowerCase()
        );

        if (!existing) {
          try {
            const allRolesRes = await fetch('/api/role-assignments', {
              credentials: 'include'
            });
            if (allRolesRes.ok) {
              const allRoles = await allRolesRes.json();
              const serverMatch = allRoles.find(
                r =>
                  r.email?.toLowerCase() === row.email.toLowerCase() &&
                  r.role === row.role &&
                  r.country?.toLowerCase() === row.country?.toLowerCase()
              );
              if (serverMatch) {
                existing = {
                  id: serverMatch.id,
                  email: serverMatch.email,
                  role: serverMatch.role,
                  status: serverMatch.status,
                  startDate: serverMatch.startDate,
                  endDate: serverMatch.endDate,
                  countries: [serverMatch.country]
                };
              }
            }
          } catch {
            // fallback — existing stays undefined
          }
        }

        if (!existing) {
          results.push({ row, status: 'duplicate', message: 'Already assigned — could not locate record to update' });
          continue;
        }

        const statusChanged    = existing.status !== row.status;
        const startDateChanged = existing.startDate !== row.startDate;
        const endDateChanged   = (existing.endDate || '') !== (row.endDate || '');

        const needsUpdate = statusChanged || startDateChanged || endDateChanged;

        if (!needsUpdate) {
          results.push({ row, status: 'duplicate', message: 'Already assigned — no changes detected' });
          continue;
        }

        const changes = [];
        if (statusChanged)    changes.push(`status: ${existing.status} → ${row.status}`);
        if (startDateChanged) changes.push(`startDate: ${existing.startDate} → ${row.startDate}`);
        if (endDateChanged)   changes.push(`endDate: ${existing.endDate || 'none'} → ${row.endDate || 'none'}`);

        try {
          const updateRes = await fetch(
            `/api/role-assignments/${existing.id}?email=${encodeURIComponent(row.email)}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                role: row.role,
                email: row.email,
                startDate: row.startDate,
                endDate: row.endDate || null,
                status: row.status,
                country: row.country,
              }),
            }
          );

          if (updateRes.ok) {
            results.push({ row, status: 'success', message: `Updated (${changes.join(', ')})` });
          } else {
            results.push({ row, status: 'error', message: `Update failed (${updateRes.status})` });
          }
        } catch {
          results.push({ row, status: 'error', message: 'Network error during update' });
        }

      } else {
        const errorData = await response.json().catch(() => ({}));
        results.push({ row, status: 'error', message: errorData.error || `Server error (${response.status})` });
      }

    } catch {
      results.push({ row, status: 'error', message: 'Network error' });
    }
  }

  setUploadResults(results);
  await fetchRoleAssignments();
  setUploadSubmitting(false);
  setUploadDone(true);
};

const resetUploadModal = () => {
  setShowUploadModal(false);
  setUploadFile(null);
  setUploadFileName('');
  setUploadRows([]);
  setUploadParsed(false);
  setUploadSubmitting(false);
  setUploadDone(false);
  setUploadDragOver(false);
  setUploadResults([]);
};

  // Build option arrays for CustomSelect
  const roleOptions = ROLES.map(r => ({ value: r, label: r }));
  const statusOptions = STATUSES.map(s => ({ value: s, label: s }));
  const statusFilterOptions = [{ value: 'All', label: 'All Status' }, ...STATUSES.map(s => ({ value: s, label: s }))];

  // ── Email options: filtered by admin's AD country access ──
  const emailOptions = getFilteredEmailOptions();

  // const countryOptions = liveCountries.map(c => ({ value: c, label: c }));
  const countryOptions = getFilteredCountryOptions();

  // Inline edit status options (disable Active if date expired)
  const editStatusOptions = STATUSES.map(s => ({
    value: s,
    label: s === 'Active' && isDateExpired(editFormData.endDate) ? 'Active (Date Expired)' : s,
    disabled: s === 'Active' && isDateExpired(editFormData.endDate)
  }));

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > delta + 2) pages.push('...');
      const start = Math.max(2, currentPage - delta);
      const end = Math.min(totalPages - 1, currentPage + delta);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - delta - 1) pages.push('...');
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="uma-wrapper">
      <div className="uma-container">
        <div className="uma-page-header">
          <h1 className="uma-page-title">Role Assignment</h1>
          <p className="uma-page-subtitle">Manage user roles, permissions, and access control</p>
        </div>

        <div className="uma-action-bar-card">
          <div className="uma-action-bar-content">
            <div className="uma-search-wrapper">
              <div className="uma-search-input-wrapper">
                <Search className="uma-search-icon" size={20} />
                <input
                  type="text"
                  placeholder="Search by email or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="uma-search-input"
                />
              </div>
            </div>

            <div className="uma-action-buttons-wrapper">
              <div style={{ minWidth: '150px' }}>
                <CustomSelect
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={statusFilterOptions}
                  placeholder="All Status"
                />
              </div>

              <button
                onClick={() => setShowAddModal(true)}
                className="uma-btn-add-user"
              >
                <Plus size={20} />
                Add User
              </button>

              {isGlobalAdmin && (
                <button
                  className="uma-btn-icon-action"
                  title="Upload Excel"
                  onClick={() => {
                    setShowUploadModal(true);
                    setUploadDone(false);
                    if (groupMembers.length === 0) fetchGroupMembers();
                  }}
                >
                  <Upload size={20} />
                </button>
              )}

              {isGlobalAdmin && (
  <button
    className="uma-btn-icon-action"
    onClick={async () => {
      const response = await fetch('/api/roles/download', {
        credentials: 'include'
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Role_Assignments_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    }}
  >
    <Download size={20} />
  </button>
)}

              <button
                className="uma-btn-icon-action"
                onClick={() => {
                  console.log('🔄 Manual refresh triggered');
                  fetchRoleAssignments();
                  fetchLiveCountries();
                  fetchAdminAssignedCountries();
                  if (showAddModal) fetchGroupMembers();
                }}
              >
                <RefreshCw size={20} />
              </button>
            </div>
          </div>
        </div>

        {loadingUsers && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>Loading role assignments...</p>
          </div>
        )}

        {!loadingUsers && (
          <div className="uma-table-card">
            <div className="uma-table-wrapper">
              <table className="uma-users-table">
                <thead>
                  <tr className="uma-table-header-row">
                    <th className="uma-table-header uma-col-role">ROLE</th>
                    <th className="uma-table-header uma-col-email">EMAIL</th>
                    <th className="uma-table-header uma-col-start-date">START DATE</th>
                    <th className="uma-table-header uma-col-end-date">END DATE</th>
                    <th className="uma-table-header uma-col-status">STATUS</th>
                    <th className="uma-table-header uma-col-country">COUNTRY</th>
                    <th className="uma-table-header uma-col-actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="uma-table-body">
                  {currentUsers.map((user) => {
                    const dateExpired = isDateExpired(editingId === user.id ? editFormData.endDate : user.endDate);

                    return (
                      <tr key={user.id} className="uma-table-row">
                        <td className="uma-table-cell uma-col-role uma-cell-role" style={{ textTransform: 'capitalize' }}>
                          {user.role}
                        </td>
                        <td className="uma-table-cell uma-col-email uma-cell-email">
                          {user.email}
                        </td>
                        <td className="uma-table-cell uma-col-start-date uma-cell-start-date">
                          {new Date(user.startDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: '2-digit'
                          })}
                        </td>
                        <td className="uma-table-cell uma-col-end-date uma-cell-end-date">
                          {editingId === user.id ? (
                            <input
                              type="date"
                              value={editFormData.endDate}
                              min={user.startDate || new Date().toISOString().split('T')[0]}
                              onChange={(e) => {
                                console.log('End date changed to:', e.target.value);
                                setEditFormData(prev => ({ ...prev, endDate: e.target.value }));
                              }}
                              className="uma-date-input"
                            />
                          ) : (
                            user.endDate ? new Date(user.endDate).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: '2-digit'
                            }) : '-'
                          )}
                        </td>
                        <td className="uma-table-cell uma-col-status">
                          {editingId === user.id ? (
                            <CustomSelect
                              value={editFormData.status}
                              onChange={(val) => {
                                console.log('Status dropdown changed to:', val);
                                setEditFormData(prev => ({ ...prev, status: val }));
                              }}
                              options={editStatusOptions}
                              className="csel-inline"
                            />
                          ) : (
                            <span className={`uma-status-badge uma-status-${user.status.toLowerCase()}`}>
                              {user.status === 'Active' ? <Check size={14} /> : <X size={14} />}
                              {user.status}
                            </span>
                          )}
                        </td>
                        <td className="uma-table-cell uma-col-country">
                          <div className="uma-countries-wrapper">
                            {user.countries.map((country, idx) => (
                              <span key={idx} className="uma-country-badge">
                                {country}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="uma-table-cell uma-col-actions uma-cell-actions">
                          <div className="uma-action-buttons">
                            {editingId === user.id ? (
                              <>
                                <button onClick={() => handleSaveEdit(user)} className="uma-btn-save" title="Save">
                                  <Check size={18} />
                                </button>
                                <button onClick={handleCancelEdit} className="uma-btn-cancel" title="Cancel">
                                  <X size={18} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => handleEditClick(user)} className="uma-btn-edit" title="Edit">
                                  <Edit2 size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && !loadingUsers && (
              <div className="uma-empty-state">
                <div className="uma-empty-state-icon">
                  <Search size={64} />
                </div>
                <h3 className="uma-empty-state-title">No users found</h3>
                <p className="uma-empty-state-text">Try adjusting your search or filters</p>
              </div>
            )}

            {filteredUsers.length > 0 && (
              <div className="uma-pagination-wrapper">
                <div className="uma-pagination-info">
                  Showing <span className="uma-pagination-count">{startIndex + 1}</span> to{' '}
                  <span className="uma-pagination-count">{Math.min(endIndex, filteredUsers.length)}</span> of{' '}
                  <span className="uma-pagination-count">{filteredUsers.length}</span> role assignments
                </div>
                <div className="uma-pagination-controls">
                  <button
                    className="uma-btn-pagination uma-btn-pagination-nav"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>

                  <div className="uma-pagination-numbers">
                    {getPageNumbers().map((pageNum, idx) =>
                      pageNum === '...' ? (
                        <span key={`ellipsis-${idx}`} className="uma-pagination-ellipsis">…</span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          className={`uma-btn-pagination-number ${currentPage === pageNum ? 'uma-btn-pagination-number-active' : ''}`}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    className="uma-btn-pagination uma-btn-pagination-nav"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && createPortal(
          <div className="uma-modal-overlay">
            <div className="uma-modal-content">
              <div className="uma-modal-header">
                <h2 className="uma-modal-title">Add New User</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      role: '',
                      email: '',
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: '',
                      status: 'Active',
                      country: ''
                    });
                  }}
                  className="uma-modal-close"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="uma-modal-body">
                {error && (
                  <div className="uma-error-message">
                    {error}
                  </div>
                )}

                <div className="uma-form-group">
                  <label className="uma-form-label">Role *</label>
                  <CustomSelect
                    value={formData.role}
                    onChange={(val) => handleFormChange('role', val)}
                    options={roleOptions}
                    placeholder="Select role..."
                  />
                </div>

                <div className="uma-form-group">
                  <label className="uma-form-label">
                    Email *
                    {loadingMembers && <span className="uma-loading-text"> (Loading...)</span>}
                    {/* ── Show access scope hint ── */}
                    {!loadingMembers && !loadingAdminCountries && (
                      <span className="uma-loading-text" style={{ marginLeft: 8 }}>
                        {isGlobalAdmin
                          ? '— showing all AD users'
                          : `— showing users from: ${adminAssignedCountries.join(', ')}`}
                      </span>
                    )}
                  </label>
                  <CustomSelect
                    value={formData.email}
                    onChange={(val) => handleFormChange('email', val)}
                    options={emailOptions}
                    placeholder={
                      loadingMembers || loadingAdminCountries
                        ? 'Loading users...'
                        : emailOptions.length === 0
                          ? 'No users found for your assigned countries'
                          : 'Select user...'
                    }
                    disabled={loadingMembers || loadingAdminCountries}
                    searchable={true}
                  />
                  <p className="uma-form-hint">
                    {isGlobalAdmin
                      ? 'All Active Directory users are available.'
                      : 'Only users from your assigned countries (based on their AD country) are shown.'}
                  </p>
                </div>

                <div className="uma-form-row">
                  <div className="uma-form-group">
                    <label className="uma-form-label">Start Date *</label>
                    <input
                      type="date"
                      className="uma-form-input"
                      value={formData.startDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => handleFormChange('startDate', e.target.value)}
                    />
                    <p className="uma-form-hint">Defaults to today</p>
                  </div>
                  <div className="uma-form-group">
                    <label className="uma-form-label">End Date (Optional)</label>
                    <input
                      type="date"
                      className="uma-form-input"
                      value={formData.endDate}
                      min={formData.startDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => handleFormChange('endDate', e.target.value)}
                    />
                  </div>
                </div>

                <div className="uma-form-group">
                  <label className="uma-form-label">Status</label>
                  <CustomSelect
                    value={formData.status}
                    onChange={(val) => handleFormChange('status', val)}
                    options={statusOptions}
                    placeholder="Select status..."
                  />
                </div>

                <div className="uma-form-group">
                  <label className="uma-form-label">
                    Country *
                    {loadingCountries && <span className="uma-loading-text"> (Loading...)</span>}
                  </label>
                  <CustomSelect
                    value={formData.country}
                    onChange={(val) => handleFormChange('country', val)}
                    options={countryOptions}
                    placeholder={loadingCountries ? 'Loading countries...' : 'Select country...'}
                    disabled={loadingCountries}
                    searchable={true}
                  />
                  <p className="uma-form-hint">Select one country for this role assignment</p>
                </div>
              </div>

              <div className="uma-modal-footer">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      role: '',
                      email: '',
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: '',
                      status: 'Active',
                      country: ''
                    });
                  }}
                  className="uma-btn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitUser}
                  className="uma-btn-modal-submit"
                >
                  Add User
                </button>
              </div>
            </div>
          </div>,
          document.body   
        )}

        {/* ============================================
            UPLOAD MODAL
            ============================================ */}
        {showUploadModal && createPortal(
          <div className="uma-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) resetUploadModal(); }}>
            <div className="uma-modal-content uma-upload-modal">
              <div className="uma-modal-header">
                <div>
                  <h2 className="uma-modal-title">Bulk Upload Users</h2>
                  <p className="uma-upload-subtitle">Upload an Excel file to add multiple role assignments at once</p>
                </div>
                <button onClick={resetUploadModal} className="uma-modal-close"><X size={24} /></button>
              </div>

              <div className="uma-modal-body">

                {!uploadParsed && !uploadDone && (
                  <>
                    <div
                      className={`uma-dropzone ${uploadDragOver ? 'uma-dropzone-active' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setUploadDragOver(true); }}
                      onDragLeave={() => setUploadDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setUploadDragOver(false);
                        handleUploadFileChange(e.dataTransfer.files[0]);
                      }}
                      onClick={() => document.getElementById('uma-file-input').click()}
                    >
                      <input
                        id="uma-file-input"
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={(e) => handleUploadFileChange(e.target.files[0])}
                      />
                      <div className="uma-dropzone-icon">
                        <Upload size={36} />
                      </div>
                      <p className="uma-dropzone-label">
                        {uploadFileName
                          ? `📄 ${uploadFileName} — parsing…`
                          : 'Drag & drop your Excel file here, or click to browse'}
                      </p>
                      <p className="uma-dropzone-hint"></p>
                    </div>
                    <div className="uma-upload-template-hint">
                      <span>Don't have a file?</span>
                      <button
                        className="uma-template-download-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadTemplate();
                        }}
                      >
                        <FileDown size={14} />
                        Download Template
                      </button>
                    </div>
                  </>
                )}

                {uploadParsed && !uploadDone && (
                  <>
                    <div className="uma-upload-summary">
                      <span className="uma-upload-summary-valid">
                         {uploadRows.filter(r => r._valid).length} valid
                      </span>
                      <span className="uma-upload-summary-invalid">
                        ❌ {uploadRows.filter(r => !r._valid).length} invalid
                      </span>
                      <span className="uma-upload-summary-total">
                        {uploadRows.length} total rows
                      </span>
                      <button
                        className="uma-upload-reselect"
                        onClick={() => {
                          setUploadParsed(false);
                          setUploadRows([]);
                          setUploadFileName('');
                          document.getElementById('uma-file-input-2').click();
                        }}
                      >
                        <Upload size={14} /> Change file
                      </button>
                      <input
                        id="uma-file-input-2"
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={(e) => handleUploadFileChange(e.target.files[0])}
                      />
                    </div>

                    <div className="uma-upload-preview-wrapper">
                      <table className="uma-upload-preview-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Role</th>
                            <th>Email</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Status</th>
                            <th>Country</th>
                            <th>Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadRows.map((row) => (
                            <tr key={row._rowNum} className={row._valid ? 'upr-valid' : 'upr-invalid'}>
                              <td className="upr-rownum">{row._rowNum}</td>
                              <td>{row.role || <span className="upr-empty">—</span>}</td>
                              <td className="upr-email">{row.email || <span className="upr-empty">—</span>}</td>
                              <td>{row.startDate || <span className="upr-empty">—</span>}</td>
                              <td>{row.endDate || <span className="upr-empty">—</span>}</td>
                              <td>{row.status}</td>
                              <td>{row.country || <span className="upr-empty">—</span>}</td>
                              <td>
                                {row._valid
                                  ? <span className="upr-badge upr-ok"><Check size={12} /> Valid</span>
                                  : <span className="upr-badge upr-err" title={row._errors.join('\n')}>
                                      ❌ {row._errors[0]}{row._errors.length > 1 ? ` +${row._errors.length - 1}` : ''}
                                    </span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {uploadDone && (
                  <div className="uma-upload-done">
                    <div className="uma-upload-done-icon"></div>
                    <h3>Upload Complete</h3>
                    <p>
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>
                         {uploadResults.filter(r => r.status === 'success').length} added
                      </span>
                      {uploadResults.filter(r => r.status === 'duplicate').length > 0 && (
                        <span style={{ color: '#d97706', fontWeight: 600, marginLeft: 16 }}>
                           {uploadResults.filter(r => r.status === 'duplicate').length} already existed
                        </span>
                      )}
                      {uploadResults.filter(r => r.status === 'error').length > 0 && (
                        <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: 16 }}>
                           {uploadResults.filter(r => r.status === 'error').length} failed
                        </span>
                      )}
                    </p>

                    {uploadResults.length > 0 && (
                      <div className="uma-upload-preview-wrapper" style={{ marginTop: 16 }}>
                        <table className="uma-upload-preview-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Role</th>
                              <th>Email</th>
                              <th>Country</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResults.map(({ row, status, message }, i) => (
                              <tr key={i} className={status === 'success' ? 'upr-valid' : 'upr-invalid'}>
                                <td className="upr-rownum">{row._rowNum}</td>
                                <td>{row.role}</td>
                                <td className="upr-email">{row.email}</td>
                                <td>{row.country}</td>
                                <td>
                                  {status === 'success' ? (
                                    <span className="upr-badge upr-ok">
                                      <Check size={12} /> {message.startsWith('Updated') ? 'Updated' : 'Added'}
                                    </span>
                                  ) : status === 'duplicate' ? (
                                    <span
                                      className="upr-badge"
                                      style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
                                      title={message}
                                    >
                                       Already Assigned
                                    </span>
                                  ) : (
                                    <span className="upr-badge upr-err" title={message}>
                                       {message}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="uma-modal-footer">
                <button onClick={resetUploadModal} className="uma-btn-modal-cancel">
                  {uploadDone ? 'Close' : 'Cancel'}
                </button>
                {uploadParsed && !uploadDone && (
                  <button
                    onClick={handleUploadConfirm}
                    className="uma-btn-modal-submit"
                    disabled={uploadSubmitting || uploadRows.filter(r => r._valid).length === 0}
                  >
                    {uploadSubmitting
                      ? 'Adding…'
                      : `Add ${uploadRows.filter(r => r._valid).length} Valid User${uploadRows.filter(r => r._valid).length !== 1 ? 's' : ''}`
                    }
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body   
        )}
      </div>
    </div>
  );
}